import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, LangProvider, ThemeProvider, UnitsProvider, useAuth } from "@/lib/providers";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CategoriesPage from "@/pages/CategoriesPage";
import PositionsPage from "@/pages/PositionsPage";
import CustomersPage from "@/pages/CustomersPage";
import QuotesListPage from "@/pages/QuotesListPage";
import SettingsPage from "@/pages/SettingsPage";
import QuoteEditorPage from "@/pages/QuoteEditorPage";
import InvoicesListPage from "@/pages/InvoicesListPage";
import InvoiceEditorPage from "@/pages/InvoiceEditorPage";
import InvoiceArchivePage from "@/pages/InvoiceArchivePage";
import AufmasseListPage from "@/pages/AufmasseListPage";
import AufmassEditorPage from "@/pages/AufmassEditorPage";
import PdfBundlePage from "@/pages/PdfBundlePage";
import UsersPage from "@/pages/UsersPage";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#737373]">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function HomeRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#737373]">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Send privileged users to dashboard, others to first available tab
  if (user.role === "admin" || user.role === "expert") return <Navigate to="/dashboard" replace />;
  if (user.role === "handworker") return <Navigate to="/quotes" replace />;
  return <Navigate to="/quotes" replace />;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#737373]">…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <UnitsProvider>
            <ConfirmProvider>
              <BrowserRouter>
              <Routes>
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                <Route path="/register" element={<Navigate to="/login" replace />} />
                <Route path="/invite/:token" element={<InviteAcceptPage />} />
                <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
                <Route path="/categories" element={<Protected><CategoriesPage /></Protected>} />
                <Route path="/positions" element={<Protected><PositionsPage /></Protected>} />
                <Route path="/customers" element={<Protected><CustomersPage /></Protected>} />
                <Route path="/users" element={<Protected><UsersPage /></Protected>} />
                <Route path="/vat-rates" element={<Navigate to="/settings" replace />} />
                <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
                <Route path="/quotes" element={<Protected><QuotesListPage /></Protected>} />
                <Route path="/quotes/new" element={<Protected><QuoteEditorPage /></Protected>} />
                <Route path="/quotes/:id" element={<Protected><QuoteEditorPage /></Protected>} />
                <Route path="/invoices" element={<Protected><InvoicesListPage /></Protected>} />
                <Route path="/invoices/archive" element={<Protected><InvoiceArchivePage /></Protected>} />
                <Route path="/invoices/new" element={<Protected><InvoiceEditorPage /></Protected>} />
                <Route path="/invoices/:id" element={<Protected><InvoiceEditorPage /></Protected>} />
                <Route path="/aufmasse" element={<Protected><AufmasseListPage /></Protected>} />
                <Route path="/aufmasse/new" element={<Protected><AufmassEditorPage /></Protected>} />
                <Route path="/aufmasse/:id" element={<Protected><AufmassEditorPage /></Protected>} />
                <Route path="/pdf-bundle" element={<Protected><PdfBundlePage /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster />
            </BrowserRouter>
            </ConfirmProvider>
          </UnitsProvider>
        </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  );
}

export default App;
